import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { Button } from "@/components/_components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/_components/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/_components/table";
import { ChevronLeft, Save, Plus, Trash2 } from "lucide-react";
import * as React from "react";
import { selectStyles } from "theme/components/select";
import { buttonStyles } from "theme/components/button";
import { toast } from "react-hot-toast";
import { UI_MESSAGES } from "@/constants/ui-constants";
import { customInstance } from "@/lib/axios-instance";
import { isAxiosError } from "axios";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";

interface JobOrderCandidate {
  id: string;
  recipientId: string;
  fullName: string;
  email: string;
  mobile: string;
  isNewlyAdded?: boolean; // Track if this is a newly added recipient
}

interface JobOrderData {
  id: number;
  jobId: string;
  campaignName: string;
  potentialCandidates: JobOrderCandidate[];
}

interface AvailableCandidate {
  id: number;
  fullName: string;
  email: string;
  mobile: string;
}

export default function JobOrderRecipientsPage() {
  return (
    <PageAccessGuard authority="ADMIN_TESTING_VIEW">
      <JobOrderRecipientsPageContent />
    </PageAccessGuard>
  );
}

function JobOrderRecipientsPageContent() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [jobOrderData, setJobOrderData] = useState<JobOrderData | null>(null);
  const [availableCandidates, setAvailableCandidates] = useState<AvailableCandidate[]>([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string>("");
  const [originalRecipientIds, setOriginalRecipientIds] = useState<Set<string>>(new Set());
  const hasFetched = useRef(false);

  useEffect(() => {
    if (id && !hasFetched.current) {
      hasFetched.current = true;
      fetchData();
    }
  }, [id]);

  const fetchData = async () => {
    if (!id) return;

    try {
      setIsLoading(true);

      // Fetch job order candidates
      const jobOrderResponse = await customInstance<JobOrderData>({
        url: `api/user/temp/job-order/candidates/id/${id}`,
        method: 'GET'
      });

      // Fetch available candidates
      const candidatesResponse = await customInstance<AvailableCandidate[]>({
        url: 'api/user/temp/candidate',
        method: 'GET'
      });

      // Track original recipients from database
      const originalIds = new Set(jobOrderResponse.data.potentialCandidates.map(c => c.id));
      setOriginalRecipientIds(originalIds);

      setJobOrderData(jobOrderResponse.data);
      setAvailableCandidates(candidatesResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      hasFetched.current = false; // Reset on error to allow retry

      let errorMessage = "Failed to load data";
      if (isAxiosError(error)) {
        const responseData = error.response?.data as { message?: string; error?: string } | undefined;
        if (responseData?.message) {
          errorMessage = `Failed to load data: ${responseData.message}`;
        } else if (responseData?.error) {
          errorMessage = `Failed to load data: ${responseData.error}`;
        } else if (error.message) {
          errorMessage = `Failed to load data: ${error.message}`;
        }
      } else if (error instanceof Error) {
        errorMessage = `Failed to load data: ${error.message}`;
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddRecipient = () => {
    if (!selectedCandidateId || !jobOrderData) return;

    const selectedCandidate = availableCandidates.find(c => c.id.toString() === selectedCandidateId);
    if (!selectedCandidate) return;

    // Check if candidate is already in the list
    const isAlreadyAdded = jobOrderData.potentialCandidates.some(c => c.id === selectedCandidateId);
    if (isAlreadyAdded) {
      toast.error("This candidate is already in the recipient list");
      return;
    }

    const newCandidate: JobOrderCandidate = {
      id: selectedCandidate.id.toString(),
      recipientId: `S${selectedCandidate.id}`,
      fullName: selectedCandidate.fullName,
      email: selectedCandidate.email,
      mobile: selectedCandidate.mobile,
      isNewlyAdded: true
    };

    setJobOrderData(prev => prev ? {
      ...prev,
      potentialCandidates: [...prev.potentialCandidates, newCandidate]
    } : null);

    setSelectedCandidateId("");
    toast.success("Recipient added successfully");
  };

  const handleRemoveRecipient = (candidateId: string) => {
    if (!jobOrderData) return;

    // Check if this is an original recipient from database
    if (originalRecipientIds.has(candidateId)) {
      toast.error("Cannot remove recipients that are already stored in the database");
      return;
    }

    setJobOrderData(prev => prev ? {
      ...prev,
      potentialCandidates: prev.potentialCandidates.filter(c => c.id !== candidateId)
    } : null);

    toast.success("Recipient removed successfully");
  };

  const handleSubmit = async () => {
    if (!jobOrderData || !id) return;

    try {
      setIsSaving(true);

      const ids = jobOrderData.potentialCandidates.map(c => c.id);

      await customInstance({
        url: `api/user/temp/job-order/candidates/id/${id}`,
        method: 'POST',
        data: { ids }
      });

      toast.success("Recipients updated successfully!");
      navigate(ROUTES.TESTING_JOB_ORDER_MGMT);
    } catch (error) {
      console.error('Error updating recipients:', error);

      let errorMessage = "Failed to update recipients";
      if (isAxiosError(error)) {
        const responseData = error.response?.data as { message?: string; error?: string } | undefined;
        if (responseData?.message) {
          errorMessage = `Failed to update recipients: ${responseData.message}`;
        } else if (responseData?.error) {
          errorMessage = `Failed to update recipients: ${responseData.error}`;
        } else if (error.message) {
          errorMessage = `Failed to update recipients: ${error.message}`;
        }
      } else if (error instanceof Error) {
        errorMessage = `Failed to update recipients: ${error.message}`;
      }

      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const getAvailableCandidatesForSelect = () => {
    if (!jobOrderData) return availableCandidates;

    const currentIds = jobOrderData.potentialCandidates.map(c => c.id);
    return availableCandidates.filter(c => !currentIds.includes(c.id.toString()));
  };

  if (isLoading) {
    return <div className="p-6">{UI_MESSAGES.LOADING}</div>;
  }

  if (!jobOrderData) {
    return <div className="p-6">Job order not found</div>;
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(ROUTES.TESTING_JOB_ORDER_MGMT)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Job Orders
        </Button>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Manage Recipients</h1>
            <div className="mt-2 space-y-1">
              <p className="text-lg font-medium text-gray-900">Job ID: {jobOrderData.jobId}</p>
              <p className="text-lg font-medium text-gray-900">Campaign: {jobOrderData.campaignName}</p>
              {jobOrderData.potentialCandidates.length < 1 && (
                <p className="text-sm text-amber-600 font-medium">
                  ⚠️ At least 1 recipient is required to submit
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving || !jobOrderData || jobOrderData.potentialCandidates.length < 1}
            className={`${buttonStyles} bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed`}
            title={jobOrderData.potentialCandidates.length < 1 ? "At least 1 recipient is required" : "Submit recipients"}
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? "Saving..." : "Submit"}
          </button>
        </div>

        {/* Add New Recipient */}
        <Card>
          <CardHeader>
            <CardTitle>Add New Recipient</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label htmlFor="candidate-select">Select Candidate</label>
                <select
                  id="candidate-select"
                  value={selectedCandidateId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedCandidateId(e.target.value)}
                  className={selectStyles}
                >
                  <option value="">Choose a candidate to add</option>
                  {getAvailableCandidatesForSelect().map((candidate) => (
                    <option key={candidate.id} value={candidate.id.toString()}>
                      {candidate.fullName} - {candidate.email}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={handleAddRecipient}
                disabled={!selectedCandidateId}
                className={`${buttonStyles} bg-green-600 hover:bg-green-700 text-white`}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Recipient
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Recipients Table */}
        <Card>
          <CardHeader>
            <CardTitle>Current Recipients ({jobOrderData.potentialCandidates.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead className="font-semibold text-gray-900">Serial No</TableHead>
                  <TableHead className="font-semibold text-gray-900">Recipient ID</TableHead>
                  <TableHead className="font-semibold text-gray-900">Full Name</TableHead>
                  <TableHead className="font-semibold text-gray-900">Email</TableHead>
                  <TableHead className="font-semibold text-gray-900">Mobile</TableHead>
                  <TableHead className="font-semibold text-gray-900">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobOrderData.potentialCandidates.map((candidate, index) => (
                  <TableRow key={candidate.id}>
                    <TableCell className="font-medium">{index + 1}</TableCell>
                    <TableCell className="font-medium">{candidate.recipientId}</TableCell>
                    <TableCell>{candidate.fullName}</TableCell>
                    <TableCell>{candidate.email}</TableCell>
                    <TableCell>{candidate.mobile}</TableCell>
                    <TableCell>
                      {originalRecipientIds.has(candidate.id) ? (
                        <span className="text-sm text-gray-500 italic">Database record</span>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveRecipient(candidate.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                          title="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {jobOrderData.potentialCandidates.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No recipients added yet. Use the form above to add recipients.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}